// Traditional Chinese (繁體中文) dictionary. Implements MktDict — same shape as en.ts.
// Numbers (47/10/7/v4/60+/9) and proper nouns / brand names stay as-is.

import type { MktDict } from "./en";

export const zhTW: MktDict = {
  nav: {
    items: {
      pricing: "價格",
      examples: "範例",
      templates: "資料庫",
      design: "設計",
      docs: "文件",
      developers: "開發者",
      blog: "部落格",
      about: "關於",
    },
    openStudio: "開啟 Studio →",
    themeToggle: "切換主題",
    language: "語言",
    status: "狀態",
    signIn: "登入",
    menu: "選單",
  },

  footer: {
    tagline: "Living applications. Living documents.",
    groups: {
      product: {
        title: "產品",
        links: {
          pricing: "價格",
          templates: "資料庫",
          docs: "文件",
          designSystem: "設計系統",
        },
      },
      company: {
        title: "公司",
        links: { about: "關於", blog: "部落格", contact: "聯絡" },
      },
      compare: {
        title: "比較",
        links: { framer: "vs Framer", wix: "vs Wix", v0: "vs V0" },
      },
      legal: {
        title: "法律",
        links: { privacy: "隱私", terms: "條款", security: "安全" },
      },
    },
    copyright: "coogo · PADO Software Design L.L.C.",
    location: "杜拜, 阿聯",
  },

  homeFooter: {
    groups: {
      product: {
        title: "產品",
        links: {
          templates: "資料庫",
          designSystem: "設計系統",
          pricing: "價格",
          studio: "Studio",
          howItWorks: "運作方式",
        },
      },
      resources: {
        title: "資源",
        links: {
          documentation: "文件",
          blog: "部落格",
          apiReference: "API 參考",
          templates: "資料庫",
        },
      },
      company: {
        title: "公司",
        links: { aboutUs: "關於我們", contact: "聯絡", careers: "招募" },
      },
      legal: {
        title: "法律",
        links: { terms: "條款", privacy: "隱私", refund: "退款" },
      },
    },
  },

  hero: {
    badge: "coogo Studio",
    h1: {
      line1: "Living applications.",
      line2: "Living documents.",
      accent: "",
    },
    subhead:
      "用一句請求建立或更新應用、文件與報告。coogo 保留來源材料、審閱脈絡與可見成果物，讓變更能被理解、確認並交付。",
    promptLines: [
      "一個帶來源材料、審閱備註和匯出狀態的活動報告…",
      "一個用客戶訪談和表格生成的分眾分析文件…",
      "把既有提案更新為可審閱的互動式工作區…",
      "把營運數據整理成可分享的決策報告…",
    ],
    inputPlaceholder: "你想建立或修改什麼？",
    generate: "詢問 coogo",
    chips: ["application", "document", "report", "review loop"],
  },

  why: {
    label: "§ 02 · why coogo",
    h2: "讓成果物持續活著的三件事。",
    intro:
      "coogo 不是把提示詞變成一次性頁面。它把意圖、來源、審閱與交付放進同一個工作流。",
    card1: {
      chip: "source material",
      h3: "來源材料和成果物一起保留。",
      body: "請求、檔案、既有畫面與決策脈絡會形成 source packet。之後修改文件、報告或應用時，不必重新解釋整段背景。",
      annLeft: "intent",
      annMid: "← source packet →",
      annRight: "artifact",
    },
    card2: {
      chip: "review loop",
      h3: "變更先可見，再採用。",
      body: "agent 可以提出修正、摘要與重組，但可見成果物仍是中心。人先確認變更，再共享、發布或匯出。",
    },
    card3: {
      chip: "design system",
      h3: "一套清楚的產品語言。",
      body: "首頁、Studio 和內容頁都使用同一個節奏：清晰留白、細線、多色但低面積的連接感，以及真正可讀的 coogo 標誌。",
      link: "查看設計系統 →",
    },
  },

  how: {
    label: "§ 03 · how it works",
    h2: "從請求到可審閱成果物。",
    intro: "四個步驟。內部生成與驗證存在，但不搶走創作者的主要畫面。",
    steps: [
      {
        n: "01",
        title: "請求",
        body: "用自然語言說明要建立或修改的內容。可以是應用、文件、報告，或既有工作的更新。",
        caption: "intent",
      },
      {
        n: "02",
        title: "補充來源",
        body: "加入檔案、資料、既有畫面或文字。coogo 將它們整理成可追蹤的來源脈絡。",
        caption: "source packet",
      },
      {
        n: "03",
        title: "審閱",
        body: "先看見成果物和建議變更。你可以接受、修改或保留目前版本。",
        caption: "review loop",
      },
      {
        n: "04",
        title: "交付",
        body: "依成果物類型分享、發布或匯出。之後的變更仍回到同一個脈絡。",
        caption: "share · publish · export",
      },
    ],
  },

  templates: {
    label: "§ 04 · live work",
    h2: "應用、文件、報告在同一個脈絡中。",
    intro:
      "Starter 不是固定樣板庫，而是工作入口。從報告、文件、應用或修正任務開始，讓 coogo 和來源材料一起工作。",
    all: "開啟資料庫",
    live: "● 線上網頁連結",
    preview: "預覽",
    webMobile: "coogo Studio",
    items: [
      { name: "建立應用", cat: "互動式工作區" },
      { name: "行銷成果報告", cat: "report" },
      { name: "客戶分眾指南", cat: "document" },
      { name: "既有工作修正", cat: "review" },
    ],
  },

  builtOn: {
    label: "§ 05 · built on",
    h2: "coogo 是人與 agent 的工作層。",
    intro:
      "coogo 將使用者意圖、來源材料、agent 建議與可見成果物連在一起。開發者細節仍存在，但預設不打斷創作者。",
    taglines: [
      "source packet",
      "visible artifact",
      "review loop",
      "share",
      "export",
    ],
  },

  faq: {
    label: "§ 06 · questions",
    h2: "人們最先問的六個問題。",
    items: [
      {
        q: "我擁有成果物嗎？",
        a: "是的。請求、來源材料、產出與匯出都屬於你的工作空間。coogo 只是協助生成、審閱與交付。",
      },
      {
        q: "只能做應用嗎？",
        a: "不是。coogo 的核心是 applications 和 documents 一起工作。報告、指南、提案、互動式工作區都可以是成果物。",
      },
      {
        q: "如果 AI 出錯了怎麼辦？",
        a: "agent 的改動以建議和可見變更呈現。你可以在採用前審閱，也可以維持目前版本。",
      },
      {
        q: "共享產出發布後如何管理？",
        a: "來源材料包、審閱記錄和匯出狀態會保存在一起，方便共享後繼續改進。",
      },
      {
        q: "我能用自己的網域嗎？",
        a: "可以。應用預設發布到 <slug>.cogo.xyz；在設定面板接入自訂網域，並把 DNS 指向我們的邊緣節點即可。",
      },
      {
        q: "定價模式是怎樣的？",
        a: "免費試用 — 無需信用卡。付款前你就能拿到可運行的連結。付費方案包含更高的 AI 點數 + 自訂網域 + 審閱功能。完整明細見價格頁。",
      },
    ],
  },

  cta: {
    label: "§ 07 · ship",
    h2: { pre: "把要活起來的工作交給 ", accent: "coogo。" },
    body: "從請求、來源檔案或粗略報告想法開始。Studio 會把它接到成果物和下一個決定。",
    start: "開啟 Studio",
    browse: "瀏覽 Library",
    chips: ["application", "document", "report", "approval loop"],
  },

  pricing: {
    label: "§ PRICING",
    h1: { line1: "一個價格，", line2: "活的產出。" },
    subhead: "應用、文件和報告都會包含審閱、共享和匯出流程。",
    billing: { monthly: "按月", annual: "按年 — 省 20%" },
    perMonth: "/月",
    customPrice: "客製",
    contactForPricing: "價格請聯絡我們",
    billedAnnually: "按年計費",
    billedMonthly: "按月計費",
    tiers: [
      {
        name: "Hobby",
        tag: "永久免費",
        cta: "免費開始",
        bullets: [
          "1 個共享產出",
          "cogo.xyz 子網域",
          "社群支援",
          "100 MB 儲存空間",
        ],
      },
      {
        name: "Creator",
        tag: "最受歡迎",
        cta: "14 天免費試用",
        bullets: [
          "10 個共享產出",
          "自訂網域",
          "審閱工作流",
          "5 GB 儲存空間",
          "電子郵件支援",
        ],
      },
      {
        name: "Studio",
        tag: "團隊適用",
        cta: "開始試用",
        bullets: [
          "50 個共享產出",
          "團隊工作區 (5 席)",
          "優先支援",
          "25 GB 儲存空間",
          "進階分析",
        ],
      },
      {
        name: "Enterprise",
        tag: "客製",
        cta: "聯絡我們",
        bullets: ["無限產出", "SSO & SAML", "SLA", "專屬客戶成功", "客製合約"],
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "在你決定之前，幾個問題",
    faq: [
      {
        q: "真的每個方案都能拿到行動應用嗎？",
        a: "是的。每個應用都附帶響應式網頁建構與 iOS/Android 就緒的原生封裝。沒有額外步驟。",
      },
      {
        q: "我能用自己的網域嗎？",
        a: "Creator 及以上可以。把網域指向 coogo，我們會自動處理 SSL。",
      },
      {
        q: "如果我降級會怎樣？",
        a: "超出新方案的應用會轉為唯讀，而不會被刪除。隨時升級即可重新啟用。",
      },
      {
        q: "共享產出會按次額外收費嗎？",
        a: "不會。方案基於共享產出、AI 點數、儲存空間和團隊能力，而不是從你的工作成果中抽成。",
      },
    ],
  },

  about: {
    label: "§ ABOUT",
    h1: { line1: "我們打造自己", line2: "可以继续修改的工作。" },
    intro:
      "coogo 始於首爾的一個週末專案。我們厭倦了看著朋友為一個網頁應用花 4 萬美元，然後還得把它重做成行動應用。於是我們讓機器一次把兩件事都做了。沒有第二次重做，沒有第二份合約，沒有第二張帳單。",
    timeline: [
      { year: "2023", label: "起步", desc: "兩名工程師，一家咖啡館。" },
      { year: "2024", label: "公開測試", desc: "8 週內 1.2 萬人註冊。" },
      {
        year: "2025",
        label: "現在",
        desc: "數千個應用已發布；首爾 + 舊金山共 40 人。",
      },
    ],
    teamLabel: "§ TEAM",
    teamTitle: "機器背後的人",
    team: [
      { name: "Chan", role: "共同創辦人 · CEO" },
      { name: "Min", role: "共同創辦人 · CTO" },
      { name: "Ha-eun", role: "設計負責人" },
      { name: "Paul", role: "成長負責人" },
    ],
    contactLabel: "§ CONTACT",
    contactTitle: "打個招呼。",
    contactIntro: "媒體、合作，或只是想聊聊 — 每封郵件我們都會回覆。",
    contactGeneral: "一般",
    contactPress: "媒體",
    contactEnterprise: "企業",
    formName: "姓名",
    formNamePlaceholder: "你的名字",
    formEmail: "電子郵件",
    formEmailPlaceholder: "you@email.com",
    formMessage: "訊息",
    formMessagePlaceholder: "告訴我們你在建構什麼…",
    formSend: "傳送訊息",
  },

  templatesPage: {
    label: "§ GALLERY",
    h1: "從真實的東西開始。",
    subhead: "選擇應用、文件、報告或修正任務的起點，再與 coogo 繼續。",
    cats: ["報告", "文件", "分析", "健康", "Starter", "SaaS", "管理", "生產力"],
  },

  blog: {
    label: "§ JOURNAL",
    h1: "田野筆記。",
    subhead: "關於建構者、創造者與軟體的大聲思考。",
    featuredSuffix: "精選",
    featuredExcerpt:
      "十年來，我看著小企業為兩個應用付費，最終只有一個能用。這是我寫下的、解釋我們為何開始打造一台不同機器的文章。",
    minRead: "分鐘",
    posts: [
      { t: "我們為何打造 coogo", tag: "Manifesto" },
      { t: "從第一天起就有原生行動端", tag: "Product" },
      { t: "形塑 coogo 的三個信念", tag: "Philosophy" },
      { t: "編輯一次，處處更新：繫結的運作原理", tag: "Engineering" },
      { t: "寫給小應用的情書", tag: "Essay" },
      { t: "Starter 聚焦：來源連接報告", tag: "Showcase" },
    ],
  },

  docs: {
    searchPlaceholder: "搜尋文件",
    sections: [
      {
        h: "快速開始",
        items: ["介紹", "建立你的第一個成果物", "共享或發布", "下一步去哪"],
      },
      {
        h: "建構",
        items: [
          "coogo JSON 概覽",
          "結構描述參考",
          "區塊",
          "事件與流程",
          "運算式",
        ],
      },
      {
        h: "ACTIONFLOW",
        items: ["概覽", "執行模式", "動作目錄 (60+)", "Validator 迴圈"],
      },
      {
        h: "STUDIO",
        items: ["概覽", "儀表板", "共享", "IDE 工作區", "主題編輯器"],
      },
      { h: "設計系統", items: ["概覽", "權杖", "來源模型", "擴充系統"] },
      {
        h: "參考",
        items: [
          "設計系統頁面",
          "元件 (進行中)",
          "來源模型 (進行中)",
          "生成 (進行中)",
          "部署 (進行中)",
          "API 參考 (進行中)",
        ],
      },
    ],
    centerLabel: "快速開始",
    h1: "介紹",
    intro:
      "coogo 是用來建立、審閱並交付應用、文件與報告的 Studio。你提供意圖與來源材料，coogo 產生可見成果物與下一步決策。",
    calloutTitle: "第一次用 coogo？",
    calloutBody:
      " 最快的路徑是把想完成的工作和手邊來源放在同一個請求裡。若有接近的 Starter，也可以從那裡開始。",
    threeTitle: "需要知道的三件事",
    three: [
      {
        title: "請求和來源一起工作。",
        body: " 檔案、筆記與既有畫面會留在同一個脈絡裡，避免每次修改都重新說明。",
      },
      {
        title: "成果物可以先審閱。",
        body: " 應用、文件與報告都以可見形態產出，確認後再共享、發布或匯出。",
      },
      {
        title: "開發者細節按需開啟。",
        body: " 一般工作畫面專注於成果物；需要時才查看結構、驗證和來源模型。",
      },
    ],
    quickStartTitle: "快速開始",
    quickStart: {
      c1: "# 1. 寫下請求",
      c2: "# 2. 加入來源",
      c3: "# 3. 審閱並共享成果物",
    },
    next: "下一步",
    nextLink: "建立你的第一個成果物 →",
    onThisPage: "本頁內容",
    toc: ["介紹", "需要知道的三件事", "快速開始"],
  },

  designSystem: {
    sidebar: {
      groups: { foundations: "基礎", system: "系統", contract: "契約" },
      items: {
        overview: "概覽",
        philosophy: "理念",
        architecture: "架構",
        tokens: "權杖",
        components: "元件",
        patterns: "模式",
        rules: "Tailwind 規則",
        ontology: "來源模型",
        schema: "JSON Schema",
        prompt: "LLM 提示",
        samples: "範例",
      },
      source: "來源",
    },
    heroChip: "coogo 設計系統 · v4",
    heroH1: { line1: "封閉的詞彙，", accent: "開放的表達。" },
    heroSubtitle: "行銷、IDE、審閱、儀表板、聊天 — 所有介面用同一套詞彙。",
    sections: {
      philosophy: {
        name: "理念",
        h2: "封閉詞彙，開放表達。",
        sub: "coogo DS 的第一原則。",
      },
      architecture: {
        name: "架構",
        h2: "一份來源，六個產物。",
        sub: "在 YAML 中編輯。其餘全部生成。",
      },
      tokens: {
        name: "權杖",
        h2: "47 個權杖，零漂移。",
        sub: "47 個權杖，零漂移。",
      },
      components: {
        name: "元件",
        h2: "一個按鈕，三個世界。",
        sub: "一個按鈕，三個世界。",
      },
      patterns: {
        name: "模式",
        h2: "10 個原型，無限產出。",
        sub: "10 個原型，無限產出。",
      },
      rules: {
        name: "Tailwind 規則",
        h2: "骨架用 className，外觀用 variant。",
        sub: "骨架用 className，外觀用 variant。",
      },
      ontology: {
        name: "來源模型",
        h2: "來源與約束，成為可見契約。",
        sub: "確認成果物根據哪些材料與規則而來。",
      },
      schema: {
        name: "JSON Schema",
        h2: "五個檔案宣告一個區塊。",
        sub: "五個檔案宣告一個區塊。",
      },
      prompt: {
        name: "LLM 提示",
        h2: "簡報 → 生成 → 驗證 → 重試。",
        sub: "簡報 → 生成 → 驗證 → 重試。",
      },
      samples: {
        name: "範例",
        h2: "一套詞彙，五個區塊。",
        sub: "同一套詞彙，五個區塊。",
      },
    },
  },

  pricingPage: {
    h1: "價格",
    subhead: "為認真對待業務的創作者打造。",
    currencyNote: "所有方案均以阿聯酋迪拉姆（AED）計費。",
    startFree: "免費開始",
    subscribeMonthly: "按月訂閱",
    subscribeYearly: "按年訂閱",
    perMonth: "/ 月",
    perYear: "/ 年",
    billingMonthly: "按月",
    billingYearly: "按年",
    yearlySaveHint: "免費 2 個月",
    yearlySaveLabel: "每年省 {amount}（立省 {pct}%）",
    reassureFree: "無需信用卡",
    reassurePaid: "隨時取消 · 安全結帳",
    errPayment: "付款失敗",
    errNoUrl: "未收到付款連結",
    errNotAuth: "未驗證",
    plans: {
      free: {
        title: "免費",
        description: "最適合：驗證第一個產品想法的創作者。",
        features: [
          "1 個共享產出",
          "每月 500 AI 額度",
          "2% 交易手續費",
          "標準支援",
        ],
        badge: "",
      },
      creator: {
        title: "Creator",
        description: "最適合：共享多個產出的團隊。",
        features: [
          "5 個共享產出",
          "每月 5,000 AI 額度",
          "審閱記錄",
          "自訂網域支援",
          "基礎分析",
        ],
        badge: "最受歡迎",
      },
      business: {
        title: "Business",
        description: "最適合：營運大量共享產出的組織。",
        features: [
          "30 個共享產出",
          "每月 20,000 AI 額度",
          "團隊審閱記錄",
          "進階分析",
          "優先支援",
        ],
        badge: "",
      },
    },
    faq: {
      title: "價格常見問題",
      items: [
        {
          q: "以什麼貨幣計費？",
          a: "所有方案均以阿聯酋迪拉姆（AED）計費。你看到的價格就是實付價格，沒有隱藏的匯率費用。",
        },
        {
          q: "按月與按年有何不同？",
          a: "按年計費可享兩個月免費——比按月支付約省 17%。可用上方的切換按鈕在按月/按年之間切換。",
        },
        {
          q: "有免費試用嗎？",
          a: "沒有限時試用。免費方案無需信用卡即可一直使用，你可以先做出一個上線的應用，需要時再升級。",
        },
        {
          q: "可以隨時取消嗎？",
          a: "可以。在帳戶設定中隨時取消——目前計費週期結束前方案仍有效，下個週期不再扣款。訂閱不可退款，詳見退款與取消政策。",
        },
        {
          q: "共享產出發布後如何管理？",
          a: "每個產出都會保留來源材料包、審閱記錄和匯出狀態，團隊可以在共享後繼續改進。",
        },
        {
          q: "可以使用自己的網域嗎？",
          a: "可以。應用預設發布到免費的 name.cogo.xyz 位址，你可以在設定面板中連接自訂網域，並將 DNS 指向我們的邊緣網路。",
        },
        {
          q: "AI 額度是什麼？",
          a: "AI 額度是你每月用於 AI 工作（生成與編輯應用）的用量額度。各方案每月分別包含 500（免費）、5,000（Creator）、20,000（Business）額度。",
        },
      ],
    },
  },

  consent: {
    title: "付款協議",
    description: "在繼續之前，請查看並同意我們的政策。",
    agreeIntro: "我同意",
    and1: "和",
    and2: "，並確認我已閱讀",
    suffix: "。",
    termsLabel: "條款與條件",
    privacyLabel: "隱私政策",
    refundLabel: "退款與取消政策",
    cancel: "取消",
    processing: "處理中...",
    proceed: "前往付款",
    close: "關閉",
  },
};
